import { useState, useEffect } from 'react';
import { TrendingDown } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export const InflationCalculator = () => {
  const [amount, setAmount] = useState(100000);
  const [inflationRate, setInflationRate] = useState(3);
  const [years, setYears] = useState(10);
  const [futureValue, setFutureValue] = useState(0);
  const [purchasingPowerLoss, setPurchasingPowerLoss] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const future = amount / Math.pow(1 + inflationRate / 100, years);
    setFutureValue(Math.round(future));
    setPurchasingPowerLoss(Math.round(amount - future));

    const data = [];
    for (let year = 0; year <= years; year++) {
      const value = amount / Math.pow(1 + inflationRate / 100, year);
      data.push({ year: year, hodnota: Math.round(value) });
    }
    setChartData(data);
  }, [amount, inflationRate, years]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{label}. rok</p>
          <p className="text-red-400 font-bold">{payload[0]?.value?.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const lossPercentage = Math.round((purchasingPowerLoss / amount) * 100);

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Za {years} let si koupíte jen za</p>
        <p className="text-4xl font-bold text-red-400">{futureValue.toLocaleString('cs-CZ')} Kč</p>
        <p className="text-gray-500 text-sm">v dnešních cenách</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Původní částka</p>
          <p className="text-white font-bold text-lg">{amount.toLocaleString('cs-CZ')} Kč</p>
        </div>
        <div className="bg-red-500/10 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Inflace ukradne</p>
          <p className="text-red-400 font-bold text-lg">-{lossPercentage}%</p>
        </div>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorInflace" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#EF4444" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="year" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="hodnota" 
              stroke="#EF4444" 
              fill="url(#colorInflace)" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
        <p className="text-red-400 text-sm">
          <span className="font-semibold">⚠ Peníze pod polštářem hoří!</span> Investujte do aktiv, která rostou rychleji než inflace.
        </p>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="inflace"
      title="Inflační kalkulačka"
      subtitle="Vliv inflace na vaše úspory"
      icon={TrendingDown}
      tip="Spořicí účet s 2% úrokem při 3% inflaci vám nepomáhá – reálně ztrácíte 1% ročně. Obrana proti inflaci: akcie, nemovitosti, dluhopisy s výnosem nad inflaci."
      result={result}
      showResult={true}
      testId="inflation-calculator"
    >
      <SliderInput
        label="Částka"
        value={amount}
        onChange={setAmount}
        min={10000}
        max={5000000}
        step={10000}
        suffix="Kč"
        testId="inflation-amount-slider"
      />
      <SliderInput
        label="Předpokládaná inflace"
        value={inflationRate}
        onChange={setInflationRate}
        min={1}
        max={15}
        step={0.5}
        suffix="%"
        formatValue={(v) => `${v.toFixed(1)} %`}
        testId="inflation-rate-slider"
      />
      <SliderInput
        label="Časový horizont"
        value={years}
        onChange={setYears}
        min={1}
        max={30}
        step={1}
        suffix="let"
        testId="inflation-years-slider"
      />
    </CalculatorCard>
  );
};
